import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BreadcrumbComponent } from "../../../../../components/breadcrumb/breadcrumb.component";
import { educationRoutes } from "../../../types/education.routes";
import { UserRoles } from "../../../../../types/userRoles";
import { GlobalContext } from "../../../../../store";
import ActionMenuComponent from "../../../../reusable-components/components/menu.component";
import {
  formatDate,
  showPopupToastMsg,
  showToastMsg,
} from "../../../../../helpers";
import { icons } from "../../../../../statics/icons";
import { services } from "../../../../../services";
import { history } from "react-router-guard";
import { ConfirmDialog } from "../../../../../dialogs/confirm-dialog/confirm-dialog";
import QuestionsFormComponent from "../questions-form/questions-form.component";
import { CreateCheckpointDialog } from "../../../modals/create-checkpoint/create-checkpoint.dialog";

const CheckpointDetailsContentComponent = ({
  checkpointId,
}: {
  checkpointId: string;
}) => {
  const { t } = useTranslation();
  const { user, setPageTitle } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [checkpointDetails, setCheckpointDetails] = useState<any>({});
  const [showEditModal, setShowEditModal] = useState(false);

  const [
    showConfirmDeleteCheckpointModal,
    setShowConfirmDeleteCheckpointModal,
  ] = useState(false);

  // ---------- Fetch Checkpoint Details ----------
  const fetchCheckpointDetails = useCallback(
    async (checkpointId: string) => {
      try {
        setLoading(true);
        const checkpoint = await services.courses.getCheckpoint(checkpointId);
        services.common.setTitle(
          `${checkpoint?.title} - ${t("PAGES_HEAD.CHECKPOINT_DETAILS")}`,
        );
        setPageTitle(t("PAGES_HEAD.EDUCATION_TITLE"));
        setCheckpointDetails(checkpoint);
      } catch (error) {
        console.error("Error fetching checkpoint details:", error);
      } finally {
        setLoading(false);
      }
    },
    [setPageTitle, t],
  );

  const deleteCheckpoint = async (checkpointId: string) => {
    const response = await services.courses.deleteCheckpoint(checkpointId);
    if (response && response.message === "SUCCESS") {
      showToastMsg(
        "success",
        t("EDUCATION_PAGE.CHECKPOINT_DELETED_SUCCESSFULLY"),
      );
      history.push(educationRoutes.course(checkpointDetails.course?.id));
    } else if (response.message) {
      showPopupToastMsg("error", t(response.message));
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
  };

  const handleConfirmDeleteCheckpoint = async (confirmed: boolean) => {
    setTimeout(() => {
      setShowConfirmDeleteCheckpointModal(false);
    }, 0);
    if (confirmed) {
      if (!checkpointId) return;
      deleteCheckpoint(checkpointId);
    }
  };

  useEffect(() => {
    fetchCheckpointDetails(checkpointId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpointId]);

  return (
    <Box className="checkpoint__details__content pb-4">
      {loading ? (
        <Skeleton variant="text" width={400} height={18} className="my-4" />
      ) : (
        <BreadcrumbComponent
          items={[
            {
              label: t("EDUCATION_PAGE.EDUCATION"),
              path: educationRoutes.courses,
              separator: "-",
            },
            {
              label: checkpointDetails.course?.title,
              path: educationRoutes.course(checkpointDetails.course?.id),
              separator: "-",
            },
            { label: checkpointDetails.title },
          ]}
        />
      )}

      <Box
        className="global__box__content mb-4 py-4"
        style={{ padding: "15px 2rem" }}
      >
        <Box
          className="d-flex justify-content-between align-items-center mb-4"
          style={{ gap: "1.9rem" }}
        >
          <Box className="info">
            {loading ? (
              <Skeleton variant="text" height={20} width={350} />
            ) : (
              <Typography variant="h1">{checkpointDetails.title}</Typography>
            )}
            {loading ? (
              <Skeleton variant="text" height={15} width={150} />
            ) : (
              <Typography
                variant="body1"
                color="textSecondary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  margin: "0.5rem 0",
                }}
              >
                <img src={icons.CALENDER} alt="calender" />{" "}
                {formatDate(
                  checkpointDetails.createdAt,
                  "dddd, DD MMM, HH:mm Z",
                )}
              </Typography>
            )}
          </Box>
          {UserRoles.FULL_ACCESS_ROLES.includes(user.role) && (
            <Box className="text-end">
              <ActionMenuComponent
                items={[
                  {
                    label: t("EDIT"),
                    onClick: () => setShowEditModal(true),
                  },
                  {
                    label: t("DELETE"),
                    onClick: () => setShowConfirmDeleteCheckpointModal(true),
                  },
                ]}
              />
              {showConfirmDeleteCheckpointModal && (
                <ConfirmDialog
                  show={showConfirmDeleteCheckpointModal}
                  onClose={handleConfirmDeleteCheckpoint}
                  dialogTitle={t("EDUCATION_PAGE.CONFIRM_DELETE")}
                  dialogBody={t(
                    "EDUCATION_PAGE.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_CHECKPOINT",
                  )}
                  confirmBtnText={t("DELETE")}
                  cancelBtn={true}
                />
              )}
            </Box>
          )}
        </Box>
        {loading ? (
          <>
            <Skeleton variant="text" width="75%" height={18} className="mb-2" />
            <Skeleton variant="text" width="50%" height={18} className="mb-2" />
            <Skeleton variant="text" width="65%" height={18} className="mb-2" />
            <Skeleton variant="text" width="40%" height={18} className="mb-2" />
            <Skeleton variant="text" width="30%" height={18} className="mb-2" />
          </>
        ) : (
          <Typography variant="body1">
            {checkpointDetails.description}
          </Typography>
        )}
      </Box>

      <QuestionsFormComponent
        checkpointId={checkpointId}
        loading={loading}
        questions={checkpointDetails.questions}
        onQuestionsSaved={() => fetchCheckpointDetails(checkpointId)}
      />

      {showEditModal && (
        <CreateCheckpointDialog
          show={showEditModal}
          onClose={(success) => {
            setShowEditModal(false);
            if (success) fetchCheckpointDetails(checkpointId);
          }}
          courseId={Number(checkpointDetails.course?.id)}
          checkpointId={Number(checkpointId)}
          checkpointData={checkpointDetails}
        />
      )}
    </Box>
  );
};

export default CheckpointDetailsContentComponent;
